import { detailsViewAriaLabel } from './peopleHub.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47582 */
import * as React from 'react';
import type { Store } from 'redux';
import { HubState, packageStateKey } from '../state';
import { connect } from 'react-redux';
import {
    safeRender,
    Region,
    RegionBoundary,
    getLogger,
    PersonType,
    ThunkDispatch,
    joinedUnifiedGroupsLeftNavId,
    ownedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
    favoritesLeftNavItemId,
    AriaLandmark,
} from '../../shared';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import PersonaList from './personaList/personaList/personaList';
import UnifiedGroupListAsync from './groups/unifiedGroupList/unifiedGroupList/unifiedGroupListAsync';
import { CurrentView } from '../models/currentViewState';
import CommandBarContainerAsync from './commandBar/commandBarContainer/commandBarContainerAsync';
import { getCurrentHubPersonas } from '../selectors/currentPersonaSelector';
import {
    getPackageState,
    listWidthUpdated,
    isActiveLeftNavItemDeletedItems,
    isInSearchMode,
    isInDeepLinkMode,
    getActiveLeftNavItemId,
    updateShowHubListStatus,
    updateRightPaneStatus,
    ClickedHubPersonaState,
} from '../../appstate';
import { AnimationClassNames } from '@fluentui/style-utilities/lib/classNames/AnimationClassNames';
import { ResizeHandle } from 'owa-resize-handle';
import { getBrowserInfo, Browser } from 'owa-user-agent';
import DeletePersonaDialogAsync from './dialogs/deletePersonaDialogAsync';
import PersonaLinkingDialogAsync from './dialogs/personaLinkingDialogAsync';
import RightContentView from './rightContentViews/rightContentView';
import RestoreContactsDialogAsync from './dialogs/restoreContactsDialogAsync';
import HardDeleteDialogAsync from './dialogs/hardDeleteDialogAsync';
import PdlEmailsPickerDialogAsync from './dialogs/pdlEmailsPickerDialogAsync';
import {
    initHubWithBootData,
    loadUserUnifiedGroups,
} from './../actions/data/groups/unifiedGroupActions';
import { LeaveGroupDialogAsync } from './dialogs/LeaveGroupDialogAsync';
import { ExportGroupDialogAsync } from './dialogs/exportGroupDialogAsync';
import type BrowserWidthBucket from 'owa-layout/lib/store/schema/BrowserWidthBucket';
import { getListContainerStyles } from '../actions/utils/listPaneStyle';
import { isCollapsedHublistOrRightPane } from '../utils/CollapsedHubListOrRightPaneStatus';

import classNames from 'classnames';
import styles from './peopleHub.scss';

export interface DataProps {
    currentView: CurrentView;
    isLeftNavVisible: boolean;
    leftNavWidth: number;
    browserWidthBucket: BrowserWidthBucket;
    listWidth: number;
    isSearchMode: boolean;
    isInFavorites: boolean;
    isInGroups: boolean;
    showRightPane: boolean;
    showHubList: boolean;
    clickedPersona: boolean;
}

export interface DispatchProps {
    onListResize: (newWidth: number) => void;
    initGroupsData: () => void;
    loadGroupsData: () => void;
    onUpdateShowHublist: (showHubList: boolean) => void;
    onUpdateShowRightPane: (showRightPane: boolean) => void;
}
const MIN_LIST_WIDTH = 260; //minimum list width for smaller size screens i.e. tablets or smaller.
const MAX_LIST_WIDTH = 520;

// smallImmersiveProfileWidth in midgard:/packages/lpc-core/src/Components/ImmersiveProfile/ImmersiveProfile.styles.web.ts
const LPC_AREA_MIN_WIDTH = 764;
const LEFT_NAV_COLLAPSED_WIDTH = 47;

function PeopleHub(props: DataProps & DispatchProps & { store: Store<{}> }): JSX.Element {
    const inCollapsedHubListOrRightPaneMode = isCollapsedHublistOrRightPane(
        props.browserWidthBucket
    );
    /* when the size of the screen is small in that scenario only hublist is shown, onclicking a person ev is
       expanded, if the clickedpersona state is undefined in that case as well we show only hublist
     */
    if (
        inCollapsedHubListOrRightPaneMode &&
        ((props.showRightPane && props.showHubList) || !props.clickedPersona)
    ) {
        props.onUpdateShowHublist(true);
        props.onUpdateShowRightPane(false);
    } else if (!inCollapsedHubListOrRightPaneMode) {
        props.onUpdateShowHublist(true);
        props.onUpdateShowRightPane(true);
    }

    const contentPaneStyle = classNames(styles.contentPane, AnimationClassNames.slideRightIn40);
    const style = classNames(
        props.showRightPane
            ? getListContainerStyles(props.listWidth, props.browserWidthBucket)
            : styles.hubListOnly,
        styles.leftColumn
    );
    const listPaneStyle = props.showHubList ? style : styles.hideHublist;

    React.useEffect(() => {
        if (isGroupsEnabled()) {
            props.initGroupsData();
            props.loadGroupsData();
        }
        if (getBrowserInfo().browser === Browser.MSIE) {
            disableTextSelectionIE();
        }
        getLogger().log('peopleHub.componentDidMount');
    }, []);

    React.useLayoutEffect(() => {
        setDocumentBodyMinWidth(props);
    }, [
        props.listWidth,
        props.leftNavWidth,
        props.isLeftNavVisible,
        props.showRightPane,
        props.showHubList,
    ]);

    return (
        <div className={styles.mainContainer}>
            <CommandBarContainerAsync />

            <div className={styles.content}>
                <div className={contentPaneStyle}>
                    <div className={listPaneStyle}>
                        {props.isInGroups ? <UnifiedGroupListAsync /> : <PersonaList />}
                    </div>
                    {props.showHubList && props.showRightPane && (
                        <ResizeHandle
                            onResize={leftWidth =>
                                onResize(
                                    leftWidth,
                                    MIN_LIST_WIDTH,
                                    MAX_LIST_WIDTH,
                                    props.onListResize
                                )
                            }
                        />
                    )}
                    {props.showRightPane && (
                        <RegionBoundary
                            region={Region.DetailsView}
                            ariaLandmark={AriaLandmark.Main}
                            ariaLabel={loc(detailsViewAriaLabel)}>
                            <div className={styles.rightContent}>
                                <RightContentView currentView={props.currentView} />
                            </div>
                        </RegionBoundary>
                    )}
                </div>
            </div>

            <DeletePersonaDialogAsync />

            <PersonaLinkingDialogAsync />

            <RestoreContactsDialogAsync />

            <HardDeleteDialogAsync />

            <LeaveGroupDialogAsync />

            <ExportGroupDialogAsync />

            <PdlEmailsPickerDialogAsync />
        </div>
    );
}

function onResize(
    width: number,
    minWidth: number,
    maxWidth: number,
    onResizeAction: (newWidth: number) => void
): void {
    let newWidth = width;
    if (width < minWidth) {
        newWidth = minWidth;
    }
    if (width > maxWidth) {
        newWidth = maxWidth;
    }
    onResizeAction(newWidth);
}

const mapStateToProps = (rootState: any): DataProps => {
    const state = getPackageState<HubState>(rootState, packageStateKey);
    const layoutState = state.appState.view.layoutState;
    const currentHubPersonas = getCurrentHubPersonas(state.appState, state.packageState);
    const isInDeletedItems = isActiveLeftNavItemDeletedItems(state.appState);
    const activeLeftNavId = getActiveLeftNavItemId(state.appState);
    const isSearchMode = isInSearchMode(state.appState);
    const isDeepLinkMode = isInDeepLinkMode(state.appState);
    const isInFavorites = activeLeftNavId === favoritesLeftNavItemId;
    const isInGroups =
        activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
        activeLeftNavId === ownedUnifiedGroupsLeftNavId ||
        activeLeftNavId === deletedUnifiedGroupsLeftNavId;
    let currentView: CurrentView;
    const isMultiplePersonaView: Boolean =
        currentHubPersonas.length > 1 || (isInDeletedItems && currentHubPersonas.length > 0);
    const isPDL: Boolean =
        currentHubPersonas?.[0] &&
        (currentHubPersonas[0].type === PersonType.PrivateDistributionList ||
            currentHubPersonas[0].type === PersonType.FavoritePrivateDistributionList);
    const isDL: Boolean =
        currentHubPersonas?.[0] && currentHubPersonas[0].type === PersonType.PublicDistributionList;
    const isGroupView: Boolean =
        (!isSearchMode &&
            (activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
                activeLeftNavId === ownedUnifiedGroupsLeftNavId)) ||
        (currentHubPersonas?.[0] &&
            (currentHubPersonas[0].type === PersonType.Group ||
                currentHubPersonas[0].type === PersonType.GroupBasicData));
    const clickedHubPersona: ClickedHubPersonaState =
        state.appState.view.navigationState.clickedHubPersona;
    const clickedPersona: boolean = !!clickedHubPersona;

    if (isMultiplePersonaView) {
        currentView = CurrentView.multiplePersonas;
    } else if (isPDL) {
        currentView = CurrentView.privateDistributionList;
    } else if (isDL) {
        currentView = CurrentView.publicDistributionList;
    } else if (!isSearchMode && activeLeftNavId === deletedUnifiedGroupsLeftNavId) {
        currentView = CurrentView.deletedGroup;
    } else if (isDeepLinkMode && !activeLeftNavId) {
        currentView = CurrentView.person;
    } else if (isGroupView) {
        currentView = CurrentView.group;
    } else {
        currentView = CurrentView.person;
    }
    return {
        currentView,
        isLeftNavVisible: layoutState.isLeftNavVisible,
        leftNavWidth: layoutState.leftPaneWidth,
        listWidth: layoutState.listWidth,
        browserWidthBucket: layoutState.browserWidthBucket,
        isSearchMode,
        isInFavorites,
        isInGroups,
        showRightPane: layoutState.showRightPane,
        showHubList: layoutState.showHubList,
        clickedPersona,
    };
};

const mapsDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => {
    return {
        onListResize: (newWidth: number) => dispatch(listWidthUpdated)(newWidth),
        loadGroupsData: () => loadUserUnifiedGroups(dispatch),
        initGroupsData: () => dispatch(initHubWithBootData()),
        onUpdateShowHublist: (showHubList: boolean) =>
            updateShowHubListStatus(dispatch)(showHubList),
        onUpdateShowRightPane: (showRightPane: boolean) =>
            updateRightPaneStatus(dispatch)(showRightPane),
    };
};

function setDocumentBodyMinWidth(props: DataProps & DispatchProps): void {
    let minWidth = props.isLeftNavVisible ? props.leftNavWidth : LEFT_NAV_COLLAPSED_WIDTH;
    if (props.showHubList && props.showRightPane) {
        minWidth += props.listWidth + LPC_AREA_MIN_WIDTH;
    } else if (props.showHubList) {
        minWidth += MIN_LIST_WIDTH;
    } else {
        minWidth += LPC_AREA_MIN_WIDTH;
    }
    document.body.style.minWidth = `${String(minWidth)}px`;
}

function disableTextSelectionIE(): void {
    document.body.classList.add(styles.disableTextSelectionIE);
}

export default connect<DataProps, DispatchProps, { store: Store<{}> }>(
    mapStateToProps,
    mapsDispatchToProps
)(safeRender(PeopleHub, 'peopleHub'));
